import { Fragment } from 'react';
import {
  Group,
  Text,
  Stack,
  Badge,
  ActionIcon,
  Menu,
  Box,
  Avatar,
  Collapse,
  ThemeIcon,
  Button,
} from '@mantine/core';
import {
  MoreVertical,
  Edit3,
  ChevronDown,
  ChevronRight,
  Send,
  Flag,
  MessageCircle,
  Users,
  RefreshCw,
} from 'react-feather';
import type { Session, CoachHuddle } from '../../../../types';
import type { SessionWithDetailsAndRsvp } from '../../../../services/api/sessions';
import { AttendanceRate } from '../../../../components/common';
import { formatDate, formatTime, formatDayOfWeek } from '../../../../utils/formatters';
import { getTimezoneAbbreviation } from '../../../../utils/timezone';
import { getGroupAvatarColor } from '../../../../utils/groupColors';
import { getAvatarColor } from '../../../../utils/avatarHelpers';
import { RsvpDisplay } from '../RsvpDisplay';
import { GroupNoTimeSlotEmptyState } from '../GroupNoTimeSlotEmptyState';
import { SurveyLineItem } from './index';
import {
  type GroupWithSessions,
  type DisplayStatus,
  getDisplayStatus,
  formatScheduleBadgeText,
} from './sessionsHelpers';
import { useFollowUpSummary } from '../../hooks/useFollowUpSummary';
import { AlertTriangle } from 'lucide-react';
import { SessionLeaderCell } from './SessionLeaderCell';
import { SessionContentStatusBadge } from './SessionContentStatusBadge';
import { BuildContentButton } from './BuildContentButton';
import type { GroupMember } from '../../../../services/api/groups';
import { GroupBookingLinks } from '../../../coach/cohort-detail/GroupBookingLinks';


interface SessionGroupCardProps {
  group: GroupWithSessions;
  cohortId: string;
  /** "flat" = border-bottom only (All tab), "card" = bordered card with radius (individual tab) */
  variant?: 'flat' | 'card';
  isExpanded: boolean;
  onToggle: (groupId: string) => void;
  onSessionClick: (session: Session) => void;
  onEditSession: (session: Session) => void;
  onOpenTopicModal: (session: SessionWithDetailsAndRsvp) => void;
  onFinalizeGroup: (groupId: string, isResend: boolean) => void;
  cohortHuddles: CoachHuddle[];
  showOneOnOnes: boolean;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  onOpenSurveyResults: (
    groupId: string,
    groupName: string,
    coachName: string | undefined,
    tab?: 'post_session' | 'midpoint' | 'endpoint'
  ) => void;
  onOpenCoachFeedback?: (surveyType: 'midpoint' | 'endpoint') => void;
  onEditHuddle?: (huddle: CoachHuddle) => void;
  onParticipantClick?: (participantId: string) => void;
  /** MED-777: Opens the Add Time Slot modal for groups without a time slot. */
  onAddTimeSlot?: () => void;
  /** MED-812 / E4.0a: Callback after a successful session-leader designation. */
  onLeaderAssigned?: () => void;
  /**
   * MED-1086 (E1.3m / AC2, AC4): per-modality live-session-delivery setting, the
   * second switch of e1.3l's gate for the per-row Build Content entry point on
   * By-Date. Threaded down to each SessionRow.
   */
  modalityLiveSessionDeliveryEnabled?: boolean;
  /** MED-1353: show the "Booking links" button, the same as the Groups view. */
  showBookingLinks?: boolean;
}

/**
 * Renders a single group card with its sessions table, huddle rows,
 * and survey line items interleaved by date.
 */
export function SessionGroupCard({
  group,
  cohortId,
  variant = 'flat',
  isExpanded,
  onToggle,
  onSessionClick,
  onEditSession,
  onOpenTopicModal,
  onFinalizeGroup,
  cohortHuddles,
  showOneOnOnes,
  onOpenSurveyResults,
  onOpenCoachFeedback,
  onEditHuddle,
  onParticipantClick,
  onOpenFollowUps,
  onAddTimeSlot,
  onLeaderAssigned,
  modalityLiveSessionDeliveryEnabled = false,
  showBookingLinks = false,
}: SessionGroupCardProps) {
  // MED-289: Compute group-level stats
  const completedSessions = group.sessions.filter((s) => s.status === 'completed').length;

  // MED-1266: pool the canonical per-session figures (attendance_total is the recorded-outcome
  // denominator, attendance_attended already includes partial/late) rather than averaging
  // per-session rates, and carry marking coverage. marked = summed recorded outcomes,
  // population = summed recorded + pending.
  const completedForAttendance = group.sessions.filter((s) => s.status === 'completed');
  const attMarked = completedForAttendance.reduce((sum, s) => sum + (s.attendance_total || 0), 0);
  const attAttended = completedForAttendance.reduce(
    (sum, s) => sum + (s.attendance_attended || 0),
    0
  );
  const attPopulation = completedForAttendance.reduce(
    (sum, s) => sum + (s.attendance_population ?? s.attendance_total ?? 0),
    0
  );
  const attendancePercent = attMarked > 0 ? Math.round((attAttended / attMarked) * 100) : null;

  // Average engagement
  const sessionsWithEngagement = group.sessions.filter((s) => s.avg_engagement != null);
  const groupAvgEngagement =
    sessionsWithEngagement.length > 0
      ? sessionsWithEngagement.reduce((sum, s) => sum + (s.avg_engagement || 0), 0) /
        sessionsWithEngagement.length
      : null;

  const scheduleBadgeText = formatScheduleBadgeText(group.scheduledTimeSlot);
  const groupAvatarColor = getGroupAvatarColor(group.name, group.id);

  // Determine callout state
  const needsFinalize = group.sessions.length > 0 && !group.isFinalized;
  // MED-838: For resend, count UNIQUE participants who have not responded to any
  // session invite. The previous reduce summed per-session pending counts, which
  // multiplied the figure by the number of sessions (e.g., 8 participants × 5
  // sessions = 40). The server now derives a per-group unique count that is
  // sourced via finalization-status; we read it directly off the group object.
  const pendingRsvpCount = group.isFinalized ? (group.pendingUniqueCount ?? 0) : 0;
  const showResendCallout = group.isFinalized && pendingRsvpCount > 0;

  // Stat block style
  const statBlockStyle = {
    border: '1px solid var(--mantine-color-gray-3)',
    borderRadius: 6,
    padding: '10px 20px',
    textAlign: 'center' as const,
    minWidth: 70,
  };

  return (
    <Box
      style={
        variant === 'card'
          ? {
              border: '1px solid #E5E2DC',
              borderRadius: 8,
              overflow: 'hidden',
              backgroundColor: '#FFFFFF',
            }
          : {
              borderBottom: '1px solid #E5E2DC',
            }
      }
    >
      {/* Group Header */}
      <Group
        justify="space-between"
        onClick={() => onToggle(group.id)}
        style={{
          cursor: 'pointer',
          backgroundColor: '#FFFFFF',
          padding: '16px 20px',
          borderBottom: isExpanded ? '1px solid #E5E2DC' : 'none',
        }}
        wrap="nowrap"
      >
        <Group gap="sm" wrap="nowrap">
          <Avatar
            size={40}
            radius="xl"
            styles={{
              root: { backgroundColor: groupAvatarColor },
              placeholder: { color: '#FFFFFF', backgroundColor: groupAvatarColor },
            }}
          >
            <Users size={20} color="white" />
          </Avatar>
          <div>
            <Text fw={600} size="sm">{group.name}</Text>
            <Text size="13px" c="dimmed">
              {group.member_count} participant{group.member_count !== 1 ? 's' : ''}
            </Text>
          </div>
          {scheduleBadgeText && (
            <Badge
              variant="light"
              color="gray"
              size="lg"
              radius="xl"
              styles={{ label: { fontWeight: 500, fontSize: 13, textTransform: 'none', letterSpacing: 0 } }}
            >
              {scheduleBadgeText}
            </Badge>
          )}
          {showBookingLinks && (
            <GroupBookingLinks
              groupId={group.id}
              groupName={group.name}
              viewer="staff"
              hasCoach={Boolean(group.coach_id)}
            />
          )}
        </Group>
        <Group gap="sm">
          {/* Bordered stat blocks */}
          <Box style={statBlockStyle}>
            <Text fw={700} size="20px" lh={1}>
              {completedSessions}/{group.sessions.length}
            </Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>
              Sessions
            </Text>
          </Box>
          <Box style={statBlockStyle}>
            <AttendanceRate
              rate={attendancePercent}
              marked={attMarked}
              population={attPopulation}
              variant="stacked"
              size="20px"
              fw={700}
            />
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>
              Attendance
            </Text>
          </Box>
          <Box style={statBlockStyle}>
            <Text fw={700} size="20px" lh={1}>
              {groupAvgEngagement !== null ? groupAvgEngagement.toFixed(1) : '—'}
            </Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }} mt={4}>
              Avg Engagement
            </Text>
          </Box>
          {/* Coach info pill */}
          {group.coach_name && (
            <Group
              gap="xs"
              style={{
                backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
                borderRadius: 6,
                padding: '14px 14px',
              }}
              wrap="nowrap"
            >
              <Avatar size="sm" radius="xl" color="medleyGreen">
                {group.coach_name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </Avatar>
              <div>
                <Text size="9px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>
                  Coach
                </Text>
                <Text size="sm" fw={500}>
                  {group.coach_name}
                </Text>
              </div>
            </Group>
          )}
          <ActionIcon variant="subtle" color="gray" size="sm">
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </ActionIcon>
        </Group>
      </Group>

      {/* Sessions Table */}
      <Collapse in={isExpanded}>
        <Box>
          {/* Invite callout banners */}
          {needsFinalize && (
            <Group
              justify="space-between"
              wrap="nowrap"
              style={{
                backgroundColor: 'var(--medley-surface-warm, #F5F4F0)',
                borderBottom: '1px solid #E5E2DC',
                padding: '6px 20px',
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size={32} radius="xl" variant="light" color="medleyGreen">
                  <Send size={14} />
                </ThemeIcon>
                <Text size="sm">
                  This group's participant list is ready.{' '}
                  <Text span fw={600}>Send invites to {group.member_count} participants.</Text>
                </Text>
              </Group>
              <Button
                variant="filled"
                size="xs"
                leftSection={<Send size={12} />}
                onClick={() => onFinalizeGroup(group.id, false)}
              >
                Finalize & Send
              </Button>
            </Group>
          )}
          {showResendCallout && (
            <Group
              justify="space-between"
              wrap="nowrap"
              style={{
                backgroundColor: 'var(--mantine-color-blue-0, #E7F5FF)',
                borderBottom: '1px solid var(--mantine-color-blue-2, #A5D8FF)',
                padding: '6px 20px',
              }}
            >
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size={32} radius="xl" variant="light" color="blue">
                  <RefreshCw size={14} />
                </ThemeIcon>
                <Text size="sm">
                  <Text span fw={600}>{pendingRsvpCount} participant{pendingRsvpCount !== 1 ? 's' : ''}</Text>
                  {' '}haven't responded to their invite.
                </Text>
              </Group>
              <Button
                variant="filled"
                size="xs"
                leftSection={<RefreshCw size={12} />}
                onClick={() => onFinalizeGroup(group.id, true)}
              >
                Resend Invites
              </Button>
            </Group>
          )}
          {group.sessions.length > 0 ? (
            <Stack gap={0}>
              <SessionGroupRows
                group={group}
                cohortId={cohortId}
                onSessionClick={onSessionClick}
                onEditSession={onEditSession}
                onOpenTopicModal={onOpenTopicModal}
                cohortHuddles={cohortHuddles}
                showOneOnOnes={showOneOnOnes}
                onOpenSurveyResults={onOpenSurveyResults}
                onOpenCoachFeedback={onOpenCoachFeedback}
                onEditHuddle={onEditHuddle}
                onParticipantClick={onParticipantClick}
                onOpenFollowUps={onOpenFollowUps}
                onLeaderAssigned={onLeaderAssigned}
                modalityLiveSessionDeliveryEnabled={modalityLiveSessionDeliveryEnabled}
              />
            </Stack>
          ) : !group.scheduledTimeSlot && onAddTimeSlot ? (
            <GroupNoTimeSlotEmptyState onAddTimeSlot={onAddTimeSlot} />
          ) : (
            <Text size="sm" c="dimmed" ta="center" py="md">
              No sessions scheduled for this group
            </Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---- Internal sub-component for table body rows ----

interface SessionGroupRowsProps {
  group: GroupWithSessions;
  cohortId: string;
  onSessionClick: (session: Session) => void;
  onEditSession: (session: Session) => void;
  onOpenTopicModal: (session: SessionWithDetailsAndRsvp) => void;
  cohortHuddles: CoachHuddle[];
  showOneOnOnes: boolean;
  onOpenSurveyResults: (
    groupId: string,
    groupName: string,
    coachName: string | undefined,
    tab?: 'post_session' | 'midpoint' | 'endpoint'
  ) => void;
  onOpenCoachFeedback?: (surveyType: 'midpoint' | 'endpoint') => void;
  onEditHuddle?: (huddle: CoachHuddle) => void;
  onParticipantClick?: (participantId: string) => void;
  onOpenFollowUps?: (options?: { sessionId?: string; participantId?: string }) => void;
  onLeaderAssigned?: () => void;
  modalityLiveSessionDeliveryEnabled?: boolean;
}

function SessionGroupRows({
  group,
  cohortId,
  onSessionClick,
  onEditSession,
  onOpenTopicModal,
  cohortHuddles,
  showOneOnOnes,
  onOpenSurveyResults,
  onOpenCoachFeedback,
  onEditHuddle,
  onParticipantClick,
  onOpenFollowUps,
  onLeaderAssigned,
  modalityLiveSessionDeliveryEnabled = false,
}: SessionGroupRowsProps) {
  const midpointIndex = Math.floor((group.sessions.length - 1) / 2);
  const totalColumns = 9;

  // E19-6: Get follow-up summary for session callouts
  const { summary: followUpSummary } = useFollowUpSummary({ cohortId });
  const sessionPendingMap = new Map<string, number>();
  if (followUpSummary) {
    for (const s of followUpSummary.sessions) {
      sessionPendingMap.set(s.session_id, s.pending_count);
    }
  }

  const handleOpenGroupSurveyModal = (tab?: 'post_session' | 'midpoint' | 'endpoint') => {
    let displayName = group.name;
    if (group.scheduledTimeSlot) {
      const ts = group.scheduledTimeSlot;
      const dayStr = formatDayOfWeek(ts.day_of_week);
      const timeStr = ts.display_time || ts.time;
      const tzAbbr = ts.timezone_abbreviation || getTimezoneAbbreviation(ts.timezone);
      displayName = `${group.name} - ${dayStr} ${timeStr} ${tzAbbr}`;
    }
    onOpenSurveyResults(group.id, displayName, group.coach_name || undefined, tab);
  };

  // MED-268: Get huddles for this group if toggle is on
  const groupHuddles = showOneOnOnes
    ? cohortHuddles.filter((h) => h.group_id === group.id && h.status !== 'cancelled')
    : [];

  // MED-698 / MED-802 E6-10b: General huddles interleave chronologically.
  // MED-1350: intro and midpoint check-ins are 1:1 rows now and show in the
  // 1:1 section, not in the group card.
  const interleavedHuddles = groupHuddles.filter(
    (h) => h.huddle_type === 'general' && !!h.scheduled_start,
  );

  // Build unified timeline items for interleaving
  type TimelineItem =
    | { type: 'session'; session: SessionWithDetailsAndRsvp; index: number }
    | { type: 'huddle'; huddle: CoachHuddle };

  const timelineItems: TimelineItem[] = [];

  group.sessions.forEach((session, index) => {
    timelineItems.push({ type: 'session', session, index });
  });

  interleavedHuddles.forEach((huddle) => {
    timelineItems.push({ type: 'huddle', huddle });
  });

  // Sort by scheduled start date
  timelineItems.sort((a, b) => {
    const dateA =
      a.type === 'session'
        ? new Date(a.session.scheduled_start || 0).getTime()
        : new Date(a.huddle.scheduled_start).getTime();
    const dateB =
      b.type === 'session'
        ? new Date(b.session.scheduled_start || 0).getTime()
        : new Date(b.huddle.scheduled_start).getTime();
    return dateA - dateB;
  });

  return (
    <>
      {timelineItems.map((item) => {
        if (item.type === 'huddle') {
          return <HuddleRow key={`huddle-${item.huddle.id}`} huddle={item.huddle} onEditHuddle={onEditHuddle} onParticipantClick={onParticipantClick} />;
        }

        const session = item.session;
        const index = item.index;
        return (
          <Fragment key={session.id}>
            <SessionRow
              session={session}
              cohortId={cohortId}
              groupMembers={group.members}
              onSessionClick={onSessionClick}
              onEditSession={onEditSession}
              onOpenTopicModal={onOpenTopicModal}
              onLeaderAssigned={onLeaderAssigned}
              modalityLiveSessionDeliveryEnabled={modalityLiveSessionDeliveryEnabled}
            />
            {/* E19-6: Pending follow-up callout */}
            {(() => {
              const pendingCount = sessionPendingMap.get(session.id) ?? 0;
              if (pendingCount <= 0) return null;
              return (
                <Box
                  px="md"
                  py={6}
                  style={{
                    background: '#FEF3E7',
                    borderBottom: '1px solid #F5D5A0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Group gap={6}>
                    <AlertTriangle size={14} color="#E9A23B" />
                    <Text size="xs" fw={600} c="#1A1A1A">
                      {pendingCount} follow-up{pendingCount !== 1 ? 's' : ''} pending
                    </Text>
                  </Group>
                  {onOpenFollowUps && (
                    <Text
                      size="xs"
                      fw={600}
                      c="#2D4739"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onOpenFollowUps({ sessionId: session.id })}
                    >
                      Review Follow Ups
                    </Text>
                  )}
                </Box>
              );
            })()}
            {/* MED-100: Midpoint survey line item after midpoint session */}
            {index === midpointIndex && (
              <SurveyLineItem
                key={`${group.id}-midpoint`}
                cohortId={cohortId}
                groupId={group.id}
                surveyType="midpoint"
                colSpan={totalColumns}
                onReview={() => handleOpenGroupSurveyModal('midpoint')}
                onReviewCoaches={onOpenCoachFeedback ? () => onOpenCoachFeedback('midpoint') : undefined}
              />
            )}
            {/* MED-100: Endpoint survey line item after last session */}
            {index === group.sessions.length - 1 && (
              <SurveyLineItem
                key={`${group.id}-endpoint`}
                cohortId={cohortId}
                groupId={group.id}
                surveyType="endpoint"
                colSpan={totalColumns}
                onReview={() => handleOpenGroupSurveyModal('endpoint')}
                onReviewCoaches={onOpenCoachFeedback ? () => onOpenCoachFeedback('endpoint') : undefined}
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
}

// ---- Row components ----

interface HuddleRowProps {
  huddle: CoachHuddle;
  onEditHuddle?: (huddle: CoachHuddle) => void;
  onParticipantClick?: (participantId: string) => void;
}

function HuddleRow({ huddle, onEditHuddle, onParticipantClick }: HuddleRowProps) {
  // MED-1350: only general huddles reach this row.
  const participantInitials = (huddle.participant_name || '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  const participantAvatarColor = getAvatarColor(huddle.participant_user_id || huddle.participant_id);

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      style={{
        backgroundColor: '#F0F4F8',
        padding: '8px 20px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <Box
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MessageCircle size={14} color="#5C7A99" />
        </Box>
        <Text size="xs" fw={600} tt="uppercase" style={{ letterSpacing: '0.3px', color: '#5C7A99', flexShrink: 0 }}>
          CHECK IN
        </Text>
        <Group
          gap="sm"
          wrap="nowrap"
          style={{ cursor: onParticipantClick && huddle.cohort_participant_id ? 'pointer' : undefined }}
          onClick={() => huddle.cohort_participant_id && onParticipantClick?.(huddle.cohort_participant_id)}
        >
          <Avatar
            size={32}
            radius="xl"
            styles={{
              root: { backgroundColor: participantAvatarColor },
              placeholder: { color: '#FFFFFF', backgroundColor: participantAvatarColor },
            }}
          >
            {participantInitials}
          </Avatar>
          <div>
            <Text size="13px" fw={500}>{huddle.participant_name}</Text>
            <Text size="10px" c="dimmed">{huddle.participant_title || '—'}</Text>
          </div>
        </Group>
      </Group>
      <Group gap="md" wrap="nowrap">
        <div style={{ textAlign: 'right' }}>
          <Text size="13px" fw={500}>{formatDate(huddle.scheduled_start)}</Text>
          <Text size="10px" c="dimmed">{formatTime(huddle.scheduled_start)} &middot; {huddle.duration_minutes} min</Text>
        </div>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={32}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<Edit3 size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEditHuddle?.(huddle);
              }}
            >
              Edit Check In
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}

interface SessionRowProps {
  session: SessionWithDetailsAndRsvp;
  cohortId: string;
  groupMembers: GroupMember[];
  onSessionClick: (session: Session) => void;
  onEditSession: (session: Session) => void;
  onOpenTopicModal: (session: SessionWithDetailsAndRsvp) => void;
  onLeaderAssigned?: () => void;
  modalityLiveSessionDeliveryEnabled?: boolean;
}

function SessionRow({
  session,
  cohortId,
  groupMembers,
  onSessionClick,
  onEditSession,
  onOpenTopicModal,
  onLeaderAssigned,
  modalityLiveSessionDeliveryEnabled = false,
}: SessionRowProps) {
  const displayStatus: DisplayStatus = getDisplayStatus(session);

  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      onClick={() => onSessionClick(session)}
      style={{
        cursor: 'pointer',
        padding: '16px 20px',
        borderBottom: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      {/* Left: Session number + Topic/Date */}
      <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
        {session.status === 'completed' ? (
          <Box
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: 'rgba(76, 175, 80, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Text size="sm" fw={600} c="#4CAF50">{session.session_number || '—'}</Text>
          </Box>
        ) : (
          <ThemeIcon size={36} radius="xl" variant="light" color="gray" style={{ flexShrink: 0 }}>
            <Text size="sm" fw={600} c="var(--mantine-color-gray-6)">{session.session_number || '—'}</Text>
          </ThemeIcon>
        )}
        <div style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="14px" fw={600}>
              {session.topic_name || `Session ${session.session_number || '—'}`}
              {!session.topic_name && (
                <Text span size="14px" c="dimmed" fw={400}>
                  {' '}(no topic set)
                </Text>
              )}
            </Text>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={20}
              onClick={(e) => {
                e.stopPropagation();
                onOpenTopicModal(session);
              }}
            >
              <Edit3 size={12} />
            </ActionIcon>
          </Group>
          <Text size="13px" c="dimmed">
            {formatDate(session.scheduled_start)}{' '}
            <span style={{ fontSize: 18, lineHeight: 1, verticalAlign: 'middle', padding: '0 2px' }}>·</span>{' '}
            {formatTime(session.scheduled_start)}
          </Text>
        </div>
        {/* MED-1075 / E1.3k: content-authoring status badge — LEFT side, following
            the session name/date (AC8). Renders nothing unless live_session_delivery
            is on and the session is group-coaching with content started. */}
        <SessionContentStatusBadge contentStatus={session.content_status} />
        {/* MED-1086 (E1.3m / AC3, AC4): Build Content on each By-Date group session
            row (repetition accepted, matching the e1.3k badge). Groups exist here →
            open the existing sessionId editor for this row's session. */}
        {session.session_number != null && (
          <BuildContentButton
            cohortId={cohortId}
            sessionNumber={session.session_number}
            sessionId={session.id}
            modalityLiveSessionDeliveryEnabled={modalityLiveSessionDeliveryEnabled}
          />
        )}
      </Group>

      {/* Right: Stats + Status + Menu */}
      <Group gap="md" wrap="nowrap">
        {/* MED-812 / E4.0a: Inline Session Leader designation for peer-led cohorts.
            Returns null for non-peer-led sessions, feature-flag-off, or when no
            scheduled_start. Click events are stopped inside the cell so the row
            click handler doesn't fire. */}
        <SessionLeaderCell
          session={session}
          cohortId={cohortId}
          groupMembers={groupMembers}
          onUpdated={onLeaderAssigned}
          liveSessionDeliveryModalityEnabled={modalityLiveSessionDeliveryEnabled}
        />
        <RsvpDisplay summary={session.rsvp_summary} isFinalized={!!session.finalized_at} />
        {session.attendance_marked && session.attendance_total != null && (
          <Box style={{ textAlign: 'center' }}>
            <Text size="sm" fw={600}>{session.attendance_attended}/{session.attendance_total}</Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>Attended</Text>
          </Box>
        )}
        {session.avg_engagement != null && (
          <Box style={{ textAlign: 'center' }}>
            <Text size="sm" fw={600}>{Number(session.avg_engagement).toFixed(1)}</Text>
            <Text size="10px" c="dimmed" tt="uppercase" style={{ letterSpacing: '0.3px' }}>Avg Engagement</Text>
          </Box>
        )}
        <Group gap="xs">
          <Badge size="sm" color={displayStatus.color} variant="light">
            {displayStatus.label}
          </Badge>
          {((session as unknown as { open_concern_count?: number }).open_concern_count ?? 0) >
            0 && (
            <Badge size="xs" variant="light" color="orange" leftSection={<Flag size={8} />}>
              {(session as unknown as { open_concern_count: number }).open_concern_count}
            </Badge>
          )}
        </Group>
        <Menu position="bottom-end" withinPortal>
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={32}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreVertical size={16} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<Edit3 size={14} />}
              onClick={(e) => {
                e.stopPropagation();
                onEditSession(session);
              }}
            >
              Edit Session
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>
    </Group>
  );
}
